/**
 * Formate un montant en FCFA
 * @param {number} amount - Montant à formater
 * @returns {string} - Montant formaté
 */
export const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return "-";

  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "XOF",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(amount)
    .replace("XOF", "FCFA");
};

/**
 * Formate un pourcentage
 * @param {number} value - Valeur à formater
 * @param {number} decimals - Nombre de décimales
 * @returns {string} - Pourcentage formaté
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === undefined || value === null) return "-";
  if (isNaN(value)) return "-";

  return `${value.toFixed(decimals)}%`;
};

/**
 * Formate un nombre
 * @param {number} value - Valeur à formater
 * @param {number} decimals - Nombre de décimales
 * @returns {string} - Nombre formaté
 */
export const formatNumber = (value, decimals = 0) => {
  if (value === undefined || value === null) return "-";
  if (isNaN(value)) return "-";

  return new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Formate une date pour l'affichage
 * @param {string|Date} date - Date à formater
 * @param {string} format - Format souhaité
 * @returns {string} - Date formatée
 */
export const formatDisplayDate = (date, format = "dd/MM/yyyy") => {
  if (!date) return "-";

  const d = new Date(date);
  if (isNaN(d.getTime())) return "-";

  const formats = {
    "dd/MM/yyyy": {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    },
    "dd/MM/yy": {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    },
    "dd MMM yyyy": {
      day: "2-digit",
      month: "short",
      year: "numeric",
    },
    "dd MMMM yyyy": {
      day: "2-digit",
      month: "long",
      year: "numeric",
    },
    "MMMM yyyy": {
      month: "long",
      year: "numeric",
    },
    "HH:mm": {
      hour: "2-digit",
      minute: "2-digit",
    },
    "dd/MM/yyyy HH:mm": {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    },
  };

  return d.toLocaleDateString(
    "fr-FR",
    formats[format] || formats["dd/MM/yyyy"],
  );
};

/**
 * Traduit un statut
 * @param {string} status - Statut à traduire
 * @param {string} type - Type de statut (appointment, payment, subscription)
 * @returns {string} - Statut traduit
 */
export const translateStatus = (status, type = "default") => {
  const translations = {
    appointment: {
      PENDING: "En attente",
      CONFIRMED: "Confirmé",
      COMPLETED: "Terminé",
      CANCELLED: "Annulé",
      PROGRESS: "En cours",
    },
    payment: {
      PENDING: "En attente",
      COMPLETED: "Complété",
      FAILED: "Échoué",
      REFUNDED: "Remboursé",
    },
    subscription: {
      ACTIVE: "Actif",
      EXPIRED: "Expiré",
      CANCELLED: "Annulé",
    },
    giftcard: {
      UNUSED: "Active",
      USED: "Partiellement utilisée",
      ALL_USED: "Utilisée",
    },
    commission: {
      pending: "En attente",
      paid: "Payé",
      cancelled: "Annulé",
    },
    default: {},
  };

  return translations[type]?.[status] || status || "-";
};

/**
 * Obtient la couleur associée à un statut
 * @param {string} status - Statut
 * @param {string} type - Type de statut
 * @returns {string} - Classe CSS de couleur
 */
export const getStatusColor = (status, type = "default") => {
  const colors = {
    appointment: {
      PENDING: "bg-yellow-100 text-yellow-800",
      CONFIRMED: "bg-blue-100 text-blue-800",
      COMPLETED: "bg-green-100 text-green-800",
      CANCELLED: "bg-red-100 text-red-800",
      PROGRESS: "bg-purple-100 text-purple-800",
    },
    payment: {
      PENDING: "bg-yellow-100 text-yellow-800",
      COMPLETED: "bg-green-100 text-green-800",
      FAILED: "bg-red-100 text-red-800",
      REFUNDED: "bg-gray-100 text-gray-800",
    },
    subscription: {
      ACTIVE: "bg-green-100 text-green-800",
      EXPIRED: "bg-gray-100 text-gray-800",
      CANCELLED: "bg-red-100 text-red-800",
    },
    default: {
      active: "bg-green-100 text-green-800",
      inactive: "bg-gray-100 text-gray-800",
    },
  };

  return colors[type]?.[status] || "bg-gray-100 text-gray-800";
};

/**
 * Tronque un texte avec des points de suspension
 * @param {string} text - Texte à tronquer
 * @param {number} length - Longueur maximale
 * @returns {string} - Texte tronqué
 */
export const truncateText = (text, length = 50) => {
  if (!text) return "-";
  if (text.length <= length) return text;
  return `${text.substring(0, length)}...`;
};

/**
 * Formate un numéro de téléphone
 * @param {string} phone - Numéro à formater
 * @returns {string} - Numéro formaté
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return "-";

  // Format sénégalais: 77 123 45 67
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 9) {
    return cleaned.replace(/(\d{2})(\d{3})(\d{2})(\d{2})/, "$1 $2 $3 $4");
  }
  return phone;
};
